"use client";

import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Search, ShoppingBag, Crown } from "lucide-react";
import { MARQUES, type Marque, type Produit } from "@/data/catalog";
import { prixProduit, formatPrix } from "./types";

/* ─────────────── Grille des 14 Maisons ─────────────── */
export function MarquesView({ onOpen }: { onOpen: (slug: string) => void }) {
  const imperiale = MARQUES.find((m) => m.imperiale)!;
  const autres = MARQUES.filter((m) => !m.imperiale);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 md:py-20">
      <div className="text-center mb-12">
        <p className="tracking-[0.35em] text-[#D4AF37] text-xs uppercase mb-3">Les échoppes de l&rsquo;Empire</p>
        <h1 className="font-display text-4xl md:text-6xl font-bold text-[#F5EFE0]">
          La <span className="gold-text">Maison Impériale</span> & ses 13 Marques
        </h1>
        <div className="gold-line w-80 mx-auto mt-5" />
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        <CarteMarque m={imperiale} imperiale onOpen={() => onOpen(imperiale.slug)} />
        {autres.map((m) => (
          <CarteMarque key={m.slug} m={m} onOpen={() => onOpen(m.slug)} />
        ))}
      </div>
    </div>
  );
}

function CarteMarque({
  m,
  onOpen,
  imperiale,
}: {
  m: Marque;
  onOpen: () => void;
  imperiale?: boolean;
}) {
  return (
    <motion.button
      onClick={onOpen}
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45 }}
      className="card-empire rounded-2xl overflow-hidden text-left group focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/60"
      aria-label={"Entrer dans la boutique " + m.nom}
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={m.image}
          alt={"Création vedette — " + m.nom}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        {imperiale && (
          <span className="absolute top-3 left-3 flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#170d02]/85 border border-[#D4AF37]/60 text-[#F6E27A] text-[11px] tracking-widest uppercase">
            <Crown className="h-3.5 w-5" /> Maison Impériale
          </span>
        )}
        <span
          className="absolute bottom-3 right-3 px-2.5 py-1 rounded-full text-[11px] font-semibold border backdrop-blur-sm"
          style={{ background: m.couleur + "CC", borderColor: m.couleurDouce, color: m.texte }}
        >
          {m.produits.length} créations
        </span>
      </div>
      <div className="p-5" style={{ background: `linear-gradient(160deg, ${m.couleur}22, transparent 60%)` }}>
        <h3 className="font-display text-lg md:text-xl font-bold text-[#F5EFE0] group-hover:text-[#F6E27A] transition-colors">
          {m.nom}
        </h3>
        <p className="text-xs md:text-sm text-[#F5EFE0]/55 mt-1.5">{m.univers}</p>
      </div>
    </motion.button>
  );
}

/* ─────────────── Page d'une marque ─────────────── */
interface DetailProps {
  marque: Marque;
  onBack: () => void;
  onAdd: (p: Produit) => void;
  onOpenLightbox: (index: number) => void;
}

export function MarqueDetail({ marque, onBack, onAdd, onOpenLightbox }: DetailProps) {
  const [q, setQ] = useState("");
  const produits = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return marque.produits;
    return marque.produits.filter(
      (p) =>
        p.name.toLowerCase().includes(t) ||
        p.contenance.toLowerCase().includes(t) ||
        String(p.num).includes(t)
    );
  }, [marque, q]);

  return (
    <div>
      {/* Bandeau de marque */}
      <div
        className="relative border-b border-[#D4AF37]/25"
        style={{ background: `linear-gradient(140deg, ${marque.couleur} 0%, #0B0710 78%)` }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 md:py-16">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-sm text-[#F5EFE0]/70 hover:text-[#F6E27A] transition-colors mb-6"
          >
            <ArrowLeft className="h-4 w-4" /> Toutes les marques
          </button>
          <div className="flex flex-col md:flex-row md:items-end gap-6 md:gap-10">
            <img
              src={marque.image}
              alt={"Création vedette — " + marque.nom}
              className="h-40 w-40 md:h-52 md:w-52 object-cover rounded-2xl border-2 shadow-[0_18px_60px_rgba(0,0,0,0.6)]"
              style={{ borderColor: marque.couleurDouce }}
            />
            <div className="flex-1">
              {marque.imperiale && (
                <p className="flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-[#F6E27A] mb-2">
                  <Crown className="h-4 w-6" /> Maison Impériale
                </p>
              )}
              <h1 className="font-display text-3xl md:text-5xl font-black text-[#F5EFE0] leading-tight">
                {marque.nom}
              </h1>
              <p className="text-[#F5EFE0]/70 mt-3 max-w-2xl">{marque.univers}</p>
              <p className="text-sm text-[#F6E27A] mt-3 font-medium">
                {marque.produits.length} créations · signature « Le Premier et le Meilleur est Adji Anta Diop »
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Recherche */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-8">
        <div className="relative max-w-md">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#D4AF37]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher une création, un format, un n°…"
            aria-label="Rechercher une création"
            className="w-full pl-10 pr-4 py-3 rounded-xl bg-white/5 border border-[#D4AF37]/30 text-[#F5EFE0] placeholder:text-[#F5EFE0]/35 focus:outline-none focus:border-[#D4AF37]/70 focus:ring-1 focus:ring-[#D4AF37]/40 text-sm"
          />
        </div>
        <p className="text-xs text-[#F5EFE0]/45 mt-3">
          {produits.length === marque.produits.length
            ? produits.length + " créations affichées"
            : produits.length + " résultat(s) sur " + marque.produits.length}
        </p>
      </div>

      {/* Grille produits */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 pb-20">
        {produits.length === 0 ? (
          <p className="text-center text-[#F5EFE0]/50 italic py-20">
            Aucune création ne répond à cette recherche…
          </p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
            {produits.map((p, i) => (
              <ProduitCard
                key={p.num}
                marque={marque}
                p={p}
                onOpen={() => onOpenLightbox(marque.produits.indexOf(p))}
                onAdd={() => onAdd(p)}
                index={i}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ProduitCard({
  marque,
  p,
  onOpen,
  onAdd,
  index,
}: {
  marque: Marque;
  p: Produit;
  onOpen: () => void;
  onAdd: () => void;
  index: number;
}) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: Math.min(index * 0.02, 0.4) }}
      className="card-empire rounded-xl overflow-hidden flex flex-col group"
    >
      <button
        onClick={onOpen}
        className="relative block aspect-square overflow-hidden focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/60"
        aria-label={"Agrandir " + p.name}
      >
        <img
          src={p.file}
          alt={p.name + " — " + marque.nom}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <span
          className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-semibold border backdrop-blur-sm"
          style={{ background: marque.couleur + "AA", borderColor: marque.couleurDouce, color: marque.texte }}
        >
          N° {String(p.num).padStart(2, "0")}
        </span>
      </button>
      <div className="p-3.5 flex flex-col gap-2 flex-1">
        <button onClick={onOpen} className="text-left focus:outline-none">
          <h3 className="text-sm font-semibold text-[#F5EFE0] leading-snug line-clamp-2 group-hover:text-[#F6E27A] transition-colors">
            {p.name}
          </h3>
        </button>
        <p className="text-[11px] text-[#F5EFE0]/50">{p.contenance || "Création de prestige"}</p>
        <div className="mt-auto flex items-center justify-between gap-2 pt-1">
          <span className="text-[#D4AF37] font-bold text-sm">{formatPrix(prixProduit(p.num))}</span>
          <button
            onClick={onAdd}
            aria-label={"Ajouter " + p.name + " au panier"}
            className="gold-btn p-2 rounded-lg"
          >
            <ShoppingBag className="h-4 w-4" />
          </button>
        </div>
      </div>
    </motion.article>
  );
}
